import React, {useState, useEffect, useContext, useMemo, createContext, useRef} from 'react';

//context
import {moveItem, removeItem as removeItemApi, getEnumFromSortTypeString, getAllListItems} from '../api/lists'
import { ListContext } from '@/context/ListContext';
import { scheduledStatus } from  '@/helpers/List';

//components
import Icon from  '@/components/elements/icons/Icons';
import ContentItemPublishedStatus from  '@/components/elements/lists/ContentItemPublishedStatus';
import ContentItemAudienceCount from  '@/components/elements/lists/ContentItemAudienceCount';
import ListContentTile from  '@/components/elements/lists/ListContentTitle';
import { ListContributorViewOptions } from '../components/elements/lists/ViewToggle';

//interfaces
import { IconLibrary } from  '@/interfaces/IconLibrary';
import { SortType, PaginationSettings, IuseContentList } from  '@/interfaces/lists';
import { NotificationStatus } from  '@/interfaces/languages';
import { getPermissionString } from  '@/helpers/Content';




const ContentListContextInitialState: any = {
    menuItems: [],
    formatTableRows: (menu: any[]) => {},
    sortType: SortType.Manual,
    setSortType: (newSortType: SortType) => {},
    updateMenuItems:(page: number) => {},
    paginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0}, 
    viewOption: ListContributorViewOptions.AllItems,
    setViewOption: (newViewOption: ListContributorViewOptions) => {},
}

export const ContentListContext = createContext(ContentListContextInitialState);

const ContentListContextProvider = ({defaultSort, children}:IuseContentList ) => {
    const top = 20;
    const [menuItems, setMenuItems] = useState<any[]>([])
    const [viewOption, setViewOption] = useState<ListContributorViewOptions>(ListContributorViewOptions.AllItems);
    const [sortType, setSortType] = useState<SortType>(getEnumFromSortTypeString(defaultSort || undefined));
    const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const {listId, appGuid, hasEditorRights, appId} = useContext(ListContext)
    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
    const [selectedItemId, setSelectedItemId] = useState<number|null>(null);
    const [contentExistsInMultipleLocations, setContentExistsInMultipleLocations] = useState<boolean>(false);
    const [displayMainMenuTable, setDisplayMainMenuTable] = useState<boolean>(false);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: null, message: ""});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    
    /* ==================== SOME X-FRAMEWORK STUFF =================== */

    // update menu items function is a closure. It's called in jquery in lists.js
    // we need to use refs to ensure that the correct and most recent values are being used in that function
    const listIdRef = useRef<string>('')
    const appGuidRef = useRef<string>('')
    const appIdRef = useRef<string>('')
    const sortTypeRef = useRef<SortType>(undefined)
    const paginationSettingsRef = useRef<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0})
    const downloadContentReportLink = (listId || listIdRef.current) ? `/a/${appGuid}/Content/DownloadPublishedContentReport?listId=${listId || listIdRef.current}` : null;
    
    // whenever appguid, listid or sorttype change in context update the refs
    useEffect(() => {
        appGuidRef.current = appGuid
        appIdRef.current = appId
        listIdRef.current = listId;
        sortTypeRef.current = sortType;
        paginationSettingsRef.current = paginationSettings

    }, [appGuid, listId, sortType, appId, paginationSettings])

    /* ====================== END OF X-FRAMEWORK STUFF ===================== */ 

    useEffect(() => {
        if(+listId > 0 && (sortType as number) >= 0) {
            updateListContentItems()
        }
    }, [listId, sortType, viewOption]);

    useEffect(() => {
        if (notificationDetails.show) {
            const timer = setTimeout(() => {
                setNotificationDetails({show: false, status:null, message: ""});
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [notificationDetails]);

    useEffect(() => {
        (window as any).functions = {...(window as any).functions, refreshMainMenu: () => updateListContentItems(paginationSettings.currentPage)}
        return () => {delete (window as any)?.functions?.refreshMainMenu}
    },[appId, listId, top, viewOption, sortType, paginationSettings])

    function updateListContentItems(page?: number){
        if (!listIdRef.current && !listId){ return }
        setDisplayMainMenuTable(true);

        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 

        getAllListItems(appIdRef.current || appId, listIdRef.current || listId, top, skip, viewOption === ListContributorViewOptions.MyItems, sortTypeRef.current)
        .then((data) =>   {
            setMenuItems(data.records);
            if (sortType === undefined) {
                setSortType(data.SortType)
            }
            setPaginationSettings({
                hasPagination: data.total > top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / top),
                pageSize: top,
                totalItems: data.total
            })
        })
        .finally(() => {
            setIsLoading(false);
        })
    }

    async function moveMenuItem(itemId, newPosition) {
        const payload = {toPosition: newPosition.toString()}
        
        try {
            await moveItem(listId, itemId, payload);
        } catch (error) {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to reorder content items, please try again."});
        }

        updateListContentItems(paginationSettings.currentPage);
    }

    async function onArchiveItemClick() {
        if (selectedItemId) {
            try {
                await removeItemApi(listId.toString(), selectedItemId.toString());
                updateListContentItems();
                setNotificationDetails({show: true, status: NotificationStatus.success, message: "Successfully archived item"});
            } catch (error) {
                setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to archive item, please try again."});
            }
            setSelectedItemId(null);
        }
        setShowArchiveModal(false);
    }

    function onOpenArchiveModal(item: any) {
        setSelectedItemId(item.id);
        setContentExistsInMultipleLocations(item.parentIdAndTitles.length > 1);
        setShowArchiveModal(true);
    }

    function formatTableRows(menuItems){
        if (!menuItems){
            return
        }
        return menuItems.map((item, idx) => ({
            content: [
                <ListContentTile 
                    key={`${item.id}-content-title`}
                    listId={listId}
                    appGuid={appGuid}
                    item={item}
                    status={<ContentItemPublishedStatus item={item}/>}
                    archiveStatus={scheduledStatus(item.scheduledPublishDate, item.scheduledArchiveDate)}
                />, 
                <ContentItemAudienceCount
                    key={`${item.id}-audience-coint`}
                    count={item.groupUserCount}
                    everyoneHasAccess={item.everyoneHasAccess}
                />,
                getPermissionString(item.permission)
            ],
            controls: hasEditorRights ? 
            [{content: <span className='archive-wrapper'><Icon iconName={IconLibrary.ARCHIVE} sizeInPx='12' color='#D12A36' /><span>Archive</span></span>, onClick: ()=> onOpenArchiveModal(item)}]
            : [],
            dragAndDropAction: (newIndex) => moveMenuItem(item.id, newIndex)
        }))
    }

    const context = useMemo(() => ({
        menuItems,
        formatTableRows,
        sortType,
        setSortType,
        updateListContentItems,
        paginationSettings,
        viewOption,
        setViewOption,
        setShowArchiveModal, 
        showArchiveModal, 
        onArchiveItemClick, 
        contentExistsInMultipleLocations, 
        displayMainMenuTable,
        notificationDetails,
        setNotificationDetails,
        isLoading,
        downloadContentReportLink
    }), [menuItems, sortType, paginationSettings, viewOption, showArchiveModal, notificationDetails, hasEditorRights, isLoading])

    return (
        <ContentListContext.Provider value={context}>
            {children}
        </ContentListContext.Provider>
    )
}

export default ContentListContextProvider